import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../AppIcon';
import useTranslation from '../../hooks/useTranslation';
import { getCurrentOrgName } from '../../lib/getCurrentOrgId';
import { formatCurrency as fmtCAD } from '../../utils/formatCurrency';

/**
 * SubcontractCombobox
 *
 * Props:
 *   subcontracts  – array of subcontract objects
 *   value         – currently selected subcontract id (string | null)
 *   onChange      – (id: string | null) => void
 *   placeholder   – string (default : t('common.searchSubcontract'))
 *   className     – extra class names for the wrapper
 *   size          – 'sm' | 'md' (default 'md')
 *   includeInternal – boolean, show internal option (default false)
 */
const SubcontractCombobox = ({
  subcontracts = [],
  value,
  onChange,
  placeholder = null,
  className = '',
  size = 'md',
  includeInternal = false,
  noProjectSelected = false,
  allowAll = true,
}) => {
  const { t } = useTranslation();
  // Le defaut ne peut pas vivre dans la signature : t() vient du hook.
  // allowAll=false : le choix devient obligatoire, il n'y a plus de « Tous »
  // vers quoi retomber, donc l'invite doit dire quoi faire.
  const resolvedPlaceholder =
    placeholder ?? (allowAll ? t('common.searchSubcontract') : t('extra.selectSubcontractorRequired'));
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const [orgName, setOrgName] = useState('');

  const inputRef = useRef(null);
  const listRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    getCurrentOrgName()?.then(name => {
      if (name) setOrgName(name);
    });
  }, []);

  const internalLabel = orgName ? `${orgName} ${t('internal.suffix')}` : '';

  const formatCurrency = (val) => fmtCAD(val);

  // Build option list
  const buildOptions = useCallback(() => {
    const opts = allowAll
      ? [{ id: '', label: t('common.all'), secondary: '', searchText: 'all' }]
      : [];
    if (includeInternal && internalLabel) {
      opts?.push({ id: '__internal__', label: internalLabel, secondary: '', searchText: `${orgName?.toLowerCase()} internal construction` });
    }
    subcontracts?.forEach(sub => {
      const name = sub?.subcontractor?.companyName || sub?.subcontractors?.company_name || sub?.name || t('common.unknownSubcontractor');
      const trade = sub?.trade || sub?.projectCost?.costCode || sub?.project_costs?.cost_code || '';
      const val = sub?.contractValue ?? sub?.contract_value ?? sub?.originalContract ?? 0;
      const label = [name, trade]?.filter(Boolean)?.join(' · ');
      opts?.push({
        id: sub?.id,
        label,
        name,
        trade,
        contractValue: val,
        searchText: `${name} ${trade}`?.toLowerCase()
      });
    });
    return opts;
  }, [subcontracts, includeInternal, internalLabel, orgName, allowAll]);

  const allOptions = buildOptions();

  // Filter by query
  const filteredOptions = query?.trim()
    ? allOptions?.filter(opt =>
        opt?.id === '' || opt?.searchText?.includes(query?.toLowerCase()?.trim())
      )
    : allOptions;

  // Resolve display value
  const selectedOption = allOptions?.find(o => o?.id === (value ?? '')) || null;
  const isLoadingOptions = subcontracts?.length === 0 && value;
  const displayValue = open
    ? query
    : isLoadingOptions
      ? t('common.loading') : (selectedOption?.label || '');

  // Calculate dropdown position using fixed coordinates
  const updateDropdownPosition = useCallback(() => {
    if (!containerRef?.current) return;
    const rect = containerRef?.current?.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = 220;
    const spaceBelow = viewportHeight - rect?.bottom;
    const spaceAbove = rect?.top;

    // Prefer below, but go above if not enough space
    const showAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

    if (showAbove) {
      setDropdownStyle({
        position: 'fixed',
        top: rect?.top - dropdownHeight - 4,
        left: rect?.left,
        width: rect?.width,
        minWidth: '200px',
        maxHeight: `${dropdownHeight}px`,
        zIndex: 9999
      });
    } else {
      setDropdownStyle({
        position: 'fixed',
        top: rect?.bottom + 4,
        left: rect?.left,
        width: rect?.width,
        minWidth: '200px',
        maxHeight: `${dropdownHeight}px`,
        zIndex: 9999
      });
    }
  }, []);

  // Update position when open
  useEffect(() => {
    if (open) {
      updateDropdownPosition();
    }
  }, [open, updateDropdownPosition]);

  // Update position on scroll/resize
  useEffect(() => {
    if (!open) return;
    const handleUpdate = () => updateDropdownPosition();
    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);
    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [open, updateDropdownPosition]);

  // Close on outside click
  useEffect(() => {
    const handleOutside = (e) => {
      if (
        containerRef?.current && !containerRef?.current?.contains(e?.target) &&
        listRef?.current && !listRef?.current?.contains(e?.target)
      ) {
        setOpen(false);
        setQuery('');
        setActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  // Scroll active item into view
  useEffect(() => {
    if (activeIndex >= 0 && listRef?.current) {
      const items = listRef?.current?.querySelectorAll('[data-option]');
      items?.[activeIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  const handleInputChange = (e) => {
    setQuery(e?.target?.value);
    setOpen(true);
    setActiveIndex(-1);
  };

  const handleInputFocus = () => {
    setOpen(true);
    setQuery('');
  };

  const handleSelect = (opt) => {
    onChange(opt?.id || null);
    setOpen(false);
    setQuery('');
    setActiveIndex(-1);
    inputRef?.current?.blur();
  };

  const handleKeyDown = (e) => {
    if (!open && (e?.key === 'ArrowDown' || e?.key === 'Enter')) {
      setOpen(true);
      return;
    }
    switch (e?.key) {
      case 'ArrowDown':
        e?.preventDefault();
        setActiveIndex(prev => Math.min(prev + 1, filteredOptions?.length - 1));
        break;
      case 'ArrowUp':
        e?.preventDefault();
        setActiveIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e?.preventDefault();
        if (activeIndex >= 0 && filteredOptions?.[activeIndex]) {
          handleSelect(filteredOptions?.[activeIndex]);
        }
        break;
      case 'Escape':
        setOpen(false);
        setQuery('');
        setActiveIndex(-1);
        inputRef?.current?.blur();
        break;
      default:
        break;
    }
  };

  const handleClear = (e) => {
    e?.stopPropagation();
    onChange(null);
    setQuery('');
    inputRef?.current?.focus();
  };

  const isSm = size === 'sm';
  const inputCls = isSm
    ? 'w-full pl-2 pr-7 py-1.5 text-xs border border-gray-200 rounded-md outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white truncate'
    : 'w-full pl-3 pr-8 py-2 text-xs border border-gray-200 rounded-lg outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white truncate';

  // ── No-project guard ──────────────────────────────────────────────────────
  if (noProjectSelected) {
    return (
      <div ref={containerRef} className={`relative ${className}`}>
        <div className={`flex items-center gap-2 ${isSm ? 'px-2 py-1.5' : 'px-3 py-2'} text-xs text-gray-400 italic border border-gray-200 rounded-lg bg-gray-50 cursor-not-allowed select-none`}>
          <Icon name="AlertCircle" size={12} className="shrink-0 text-gray-300" />
          <span>{t('subcontractCombobox.selectProjectFirst')}</span>
        </div>
      </div>
    );
  }

  const dropdownContent = open ? (
    <div
      ref={listRef}
      style={{ ...dropdownStyle, overflowY: 'auto' }}
      className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
    >
      {filteredOptions?.length === 0 ? (
        <div className="px-3 py-2.5 text-xs text-gray-400 italic">{t('common.noResults')}</div>
      ) : (
        filteredOptions?.map((opt, idx) => {
          const isSelected = (value ?? '') === opt?.id;
          const isActive = idx === activeIndex;
          return (
            <button
              key={opt?.id ?? 'none'}
              data-option
              type="button"
              onMouseDown={() => handleSelect(opt)}
              onMouseEnter={() => setActiveIndex(idx)}
              className={`w-full text-left px-3 py-2 text-xs transition-colors flex items-center justify-between gap-2 ${
                isActive ? 'bg-primary/10 text-primary' :
                isSelected ? 'bg-orange-50 text-primary': 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span className={`truncate ${opt?.id === '' ? 'text-gray-400 italic' : ''}`}>
                {opt?.label}
              </span>
              {isSelected && (
                <Icon name="Check" size={10} className="shrink-0 text-primary" />
              )}
            </button>
          );
        })
      )}
    </div>
  ) : null;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Input */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={resolvedPlaceholder}
          autoComplete="off"
          className={`${inputCls}${isLoadingOptions ? ' text-gray-400 italic' : ''}`}
        />
        {/* Chevron / Clear */}
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
          {value && value !== '' ? (
            <button
              type="button"
              onMouseDown={handleClear}
              className="p-0.5 rounded text-gray-300 hover:text-gray-500 transition-colors"
              tabIndex={-1}
            >
              <Icon name="X" size={isSm ? 10 : 12} />
            </button>
          ) : (
            <Icon name={open ? 'ChevronUp' : 'ChevronDown'} size={isSm ? 10 : 12} className="text-gray-400 pointer-events-none" />
          )}
        </div>
      </div>

      {/* Dropdown rendered via portal to avoid clipping */}
      {typeof document !== 'undefined' && createPortal(dropdownContent, document.body)}
    </div>
  );
};

export default SubcontractCombobox;
